// src/components/Phaser/adapters/python/shim.ts
import type { Controls, EngineBridge } from '../../core/types';
import { getPyodide } from './pyodideLoader';

type LogForwarder = {
    log: (msg: string) => void;
    info: (msg: string) => void;
    warn: (msg: string) => void;
    error: (msg: string) => void;
    complete: () => void;
};

const makeLogForwarder = (bridge?: EngineBridge): LogForwarder => {
    const safe = <T extends any[]>(fn?: (...a: T) => void, ...args: T) => { try { fn?.(...args); } catch { } };
    const wb = (window as any).__IDE_BRIDGE__;
    return {
        log: (m) => (bridge as any)?.onLog ? safe((bridge as any).onLog, m) : safe(wb?.onLog, m),
        info: (m) => (bridge as any)?.onInfo ? safe((bridge as any).onInfo, m) : safe(wb?.onInfo ?? wb?.onLog, m),
        warn: (m) => (bridge as any)?.onWarn ? safe((bridge as any).onWarn, m) : safe(wb?.onWarn ?? wb?.onLog, m),
        error: (m) => (bridge as any)?.onError ? safe((bridge as any).onError, m) : safe(wb?.onError ?? wb?.onLog, m),
        complete: () => (bridge as any)?.onComplete ? safe((bridge as any).onComplete) : safe(wb?.onComplete),
    };
};

type ListenerDisposer = () => void;

export interface PYGlue {
    compile: (code: string, bridge: EngineBridge, controls: Controls) => Promise<any>;
    runAll: (exec: any, controls: Controls) => Promise<void>;
    stop?: (exec: any) => Promise<void>;
}

type FnEntry = { fn: Function; owner: any };
type FnMap = Record<string, FnEntry>;

// ------- resolve API -------
async function resolveDefaultIdAsync(bridge?: any, tries = 20, stepMs = 25): Promise<any> {
    if (CACHED_DEFAULT_ID != null) return CACHED_DEFAULT_ID;

    const api = engineApiFrom(bridge);
    const toArr = (v: any) => (Array.isArray(v) ? v : []);

    // 1) API dédiée si dispo
    try {
        const maybeDefault = api?.getDefaultCharacterId?.();
        const def = (maybeDefault && typeof maybeDefault.then === 'function')
            ? await maybeDefault
            : maybeDefault;
        if (def != null && def !== '') { setDefaultId(def); return CACHED_DEFAULT_ID; }
    } catch { }

    // 2) Fallback: 'player' puis liste d'ids (tolère Promises)
    for (let i = 0; i < tries && CACHED_DEFAULT_ID == null; i++) {
        try {
            if (api?.getCharacter) {
                const maybePlayer = api.getCharacter('player');
                const player = (maybePlayer && typeof maybePlayer.then === 'function')
                    ? await maybePlayer
                    : maybePlayer;
                if (player?.id != null) { setDefaultId(player.id); break; }
            }
            if (api?.getAllCharacterIds) {
                const maybeIds = api.getAllCharacterIds();
                const ids = (maybeIds && typeof maybeIds.then === 'function')
                    ? toArr(await maybeIds)
                    : toArr(maybeIds);
                if (ids.length) { setDefaultId(ids[0]); break; }
            }
        } catch { }
        await new Promise(r => setTimeout(r, stepMs));
    }
    return CACHED_DEFAULT_ID;
}
function addFnsFrom(source: any, into: FnMap) {
    if (!source || typeof source !== 'object') return;
    for (const k of Object.keys(source)) {
        if (k in into) continue;
        const v = (source as any)[k];
        if (typeof v === 'function') into[k] = { fn: v, owner: source };
    }
}
function engineApiFrom(bridge?: EngineBridge) {
    const g = globalThis as any;
    return (bridge as any)?.api ?? g.__IDE_GLOBALS__ ?? g.__IDE_BRIDGE__?.api ?? g.engine?.api ?? g.API ?? {};
}
function resolveFunctionMap(bridge?: EngineBridge): FnMap {
    const out: FnMap = {};
    const g = globalThis as any;
    addFnsFrom(g.__IDE_GLOBALS__, out);
    if (bridge && typeof bridge === 'object') { addFnsFrom(bridge, out); addFnsFrom((bridge as any).api, out); }
    addFnsFrom(g.__IDE_BRIDGE__, out);
    addFnsFrom(g.__IDE_BRIDGE__?.api, out);
    addFnsFrom(g.TILMI_API, out);
    addFnsFrom(g.API, out);
    return out;
}

// ------- fast default id cache -------
let CACHED_DEFAULT_ID: string | number | undefined;
const normalizeId = (id: any) => (typeof id === 'string' && /^\d+$/.test(id) ? Number(id) : id);
const setDefaultId = (id: any) => { if (id !== undefined && id !== null && id !== '') CACHED_DEFAULT_ID = normalizeId(id); };
const clearDefaultId = () => { CACHED_DEFAULT_ID = undefined; };

function _engineGetCharacter(bridge?: any, id?: any) {
    try {
        const api = engineApiFrom(bridge);
        return api?.getCharacter?.(id) ?? null;
    } catch { return null; }
}
function _engineGetAllCharacterIds(bridge?: any): Array<string | number> {
    try {
        const api = engineApiFrom(bridge);
        const ids = api?.getAllCharacterIds?.() ?? [];
        return Array.isArray(ids) ? ids : [];
    } catch { return []; }
}
function _engineDefaultCharId(bridge?: any) {
    try {
        const api = engineApiFrom(bridge);
        console.log("api", api)
        const player = api?.getCharacter?.('player');
        if (player?.id != null) return player.id;
        const ids = _engineGetAllCharacterIds(bridge);
        console.log("ids", ids)
        if (ids.length) return ids[0];
    } catch { }
    return undefined;
}
function getDefaultIdFast(bridge?: any) {
    if (CACHED_DEFAULT_ID != null) return CACHED_DEFAULT_ID;
    const id = _engineDefaultCharId(bridge);
    if (id != null) { setDefaultId(id); return CACHED_DEFAULT_ID; }
    return undefined;
}

// attente “light”
async function waitForCharacterId(id: any, bridge?: any, timeoutMs = 800, stepMs = 16) {
    const nid = normalizeId(id);
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
        try {
            const maybe = _engineGetCharacter(bridge, nid);
            if (maybe && typeof (maybe as any).then === 'function') {
                const c = await (maybe as any);
                if (c) return nid;
            } else if (maybe) {
                return nid;
            }
        } catch { }
        await new Promise(r => setTimeout(r, stepMs));
    }
    return nid;
}

// ------- per-character action queue -------
const __PY_ACTION_QUEUES__ = new Map<string | number, Promise<any>>();
function queueOp(id: string | number, op: (nid: any) => Promise<any>, bridge?: any) {
    const nid = normalizeId(id);
    const prev = __PY_ACTION_QUEUES__.get(nid) || Promise.resolve();
    const next = prev.catch(() => { }).then(async () => {
        const realId = await waitForCharacterId(nid, bridge);
        return op(realId);
    });
    __PY_ACTION_QUEUES__.set(nid, next);
    return next;
}

// ------- movement overrides (fast) -------
// NOTE: on renvoie la liste des NOMS exposés qui sont asynchrones → utilisé pour l'auto-await Python.
function overrideMovementForPython(map: FnMap, bridge?: EngineBridge): string[] {
    const asyncNames: string[] = [];
    const api = engineApiFrom(bridge);
    const turn = api?.turnCharacter;
    const walk = api?.walkCharacter;
    const jump = api?.jumpCharacter;
    if (typeof turn !== 'function' || typeof walk !== 'function' || typeof jump !== 'function') return asyncNames;

    // wrap addCharacter to cache default id immediately
    const wrapAdd = (name: string) => {
        const src = (map as any)[name] as FnEntry | undefined;
        const base = src?.fn ?? (api as any)[name];
        if (typeof base === 'function') {
            (map as any)[name] = {
                owner: api,
                fn: (...args: any[]) => {
                    const ret = base.apply(api, args);
                    if (ret && typeof (ret as any).then === 'function') {
                        return (ret as Promise<any>).then((id) => { setDefaultId(id); return id; });
                    }
                    setDefaultId(ret);
                    return ret;
                }
            };
        }
    };
    wrapAdd('addCharacter');
    wrapAdd('addCharacterWithSize');

    // reset cache on setScene
    const srcSetScene = (map as any)['setScene'] as FnEntry | undefined;
    if (srcSetScene?.fn) {
        (map as any)['setScene'] = {
            owner: srcSetScene.owner,
            fn: (...a: any[]) => { clearDefaultId(); __PY_ACTION_QUEUES__.clear(); return srcSetScene.fn.apply(srcSetScene.owner, a); }
        };
    }

    const set = (name: string, fn: Function, isAsync = true) => {
        map[name] = { fn, owner: api };
        if (isAsync) asyncNames.push(name);
    };

    const deg = { right: 0, left: 180, up: 90, down: 270, ru: 45, rd: 315, lu: 135, ld: 225 };
    const step = 1, diag = Math.SQRT2;

    const target = (maybeId: any) => (maybeId === undefined || maybeId === null || maybeId === '')
        ? (getDefaultIdFast(bridge) as any)
        : maybeId;

    // byId & aliases — tolère id manquant (utilise default)
    set('walk_byId', (id: any, d = step) => queueOp(target(id), (nid) => walk(nid, d), bridge));
    set('walk_by_id', (map['walk_byId'] as any).fn);

    const wdir = (angle: number, dist = step) => (id: any) =>
        queueOp(target(id), async (nid) => { await turn(nid, '', angle); return walk(nid, dist); }, bridge);

    set('walk_right_byId', wdir(deg.right)); set('walk_right_by_id', (map['walk_right_byId'] as any).fn);
    set('walk_left_byId', wdir(deg.left)); set('walk_left_by_id', (map['walk_left_byId'] as any).fn);
    set('walk_up_byId', wdir(deg.up)); set('walk_up_by_id', (map['walk_up_byId'] as any).fn);
    set('walk_down_byId', wdir(deg.down)); set('walk_down_by_id', (map['walk_down_byId'] as any).fn);
    set('walk_right_up_byId', wdir(deg.ru, diag)); set('walk_right_up_by_id', (map['walk_right_up_byId'] as any).fn);
    set('walk_right_down_byId', wdir(deg.rd, diag)); set('walk_right_down_by_id', (map['walk_right_down_byId'] as any).fn);
    set('walk_left_up_byId', wdir(deg.lu, diag)); set('walk_left_up_by_id', (map['walk_left_up_byId'] as any).fn);
    set('walk_left_down_byId', wdir(deg.ld, diag)); set('walk_left_down_by_id', (map['walk_left_down_byId'] as any).fn);

    set('walk_forward_byId', (id: any) => queueOp(target(id), (nid) => walk(nid, step), bridge));
    set('walk_forward_by_id', (map['walk_forward_byId'] as any).fn);
    set('walk_backward_byId', (id: any) => queueOp(target(id), async (nid) => { await turn(nid, 'BACKWARD', 0); return walk(nid, step); }, bridge));
    set('walk_backward_by_id', (map['walk_backward_byId'] as any).fn);

    set('walk_choice_byId', (id: any, dir: 'FORWARD' | 'BACKWARD' = 'FORWARD') =>
        queueOp(target(id), async (nid) => {
            if (dir === 'FORWARD') return walk(nid, step);
            await turn(nid, 'BACKWARD', 0); return walk(nid, step);
        }, bridge)
    );
    set('walk_choice_by_unit_byId', (id: any, dir: 'FORWARD' | 'BACKWARD', d: number) =>
        queueOp(target(id), async (nid) => {
            if (dir === 'FORWARD') return walk(nid, d);
            await turn(nid, 'BACKWARD', 0); return walk(nid, d);
        }, bridge)
    );

    set('jump_byId', (id: any, h = step) => queueOp(target(id), (nid) => jump(nid, h), bridge));
    set('jump_by_id', (map['jump_byId'] as any).fn);

    const jdir = (angle: number, dist = step) => (id: any) =>
        queueOp(target(id), async (nid) => { await turn(nid, '', angle); return jump(nid, dist); }, bridge);

    set('jump_right_byId', jdir(deg.right)); set('jump_right_by_id', (map['jump_right_byId'] as any).fn);
    set('jump_left_byId', jdir(deg.left)); set('jump_left_by_id', (map['jump_left_byId'] as any).fn);
    set('jump_up_byId', jdir(deg.up)); set('jump_up_by_id', (map['jump_up_byId'] as any).fn);
    set('jump_down_byId', jdir(deg.down)); set('jump_down_by_id', (map['jump_down_byId'] as any).fn);
    set('jump_right_up_byId', jdir(deg.ru, diag)); set('jump_right_up_by_id', (map['jump_right_up_byId'] as any).fn);
    set('jump_right_down_byId', jdir(deg.rd, diag)); set('jump_right_down_by_id', (map['jump_right_down_byId'] as any).fn);
    set('jump_left_up_byId', jdir(deg.lu, diag)); set('jump_left_up_by_id', (map['jump_left_up_byId'] as any).fn);
    set('jump_left_down_byId', jdir(deg.ld, diag)); set('jump_left_down_by_id', (map['jump_left_down_byId'] as any).fn);

    set('jump_forward_byId', (id: any) => queueOp(target(id), (nid) => jump(nid, step), bridge));
    set('jump_forward_by_id', (map['jump_forward_byId'] as any).fn);
    set('jump_backward_byId', (id: any) => queueOp(target(id), async (nid) => { await turn(nid, 'BACKWARD', 0); return jump(nid, step); }, bridge));
    set('jump_backward_by_id', (map['jump_backward_byId'] as any).fn);

    set('jump_choice_byId', (id: any, dir: 'FORWARD' | 'BACKWARD' = 'FORWARD') =>
        queueOp(target(id), async (nid) => {
            if (dir === 'FORWARD') return jump(nid, step);
            await turn(nid, 'BACKWARD', 0); return jump(nid, step);
        }, bridge)
    );
    set('jump_choice_by_unit_byId', (id: any, dir: 'FORWARD' | 'BACKWARD', d: number) =>
        queueOp(target(id), async (nid) => {
            if (dir === 'FORWARD') return jump(nid, d);
            await turn(nid, 'BACKWARD', 0); return jump(nid, d);
        }, bridge)
    );

    set('turn_byId', (id: any, angle = 90) => queueOp(target(id), (nid) => turn(nid, '', angle), bridge));
    set('turn_by_id', (map['turn_byId'] as any).fn);
    set('turn_right_or_left_byId', (id: any, dir: 'RIGHT' | 'LEFT' = 'RIGHT') => queueOp(target(id), (nid) => turn(nid, dir, 90), bridge));
    set('turn_right_or_left_by_id', (map['turn_right_or_left_byId'] as any).fn);
    set('choose_turn_angle_byId', (id: any, dir: 'RIGHT' | 'LEFT' = 'RIGHT', angle = 90) => queueOp(target(id), (nid) => turn(nid, dir, angle), bridge));
    set('choose_turn_angle_by_id', (map['choose_turn_angle_byId'] as any).fn);

    // no-id versions
    // helpers directionnels (sans id) — version ASYNC
    const wdef = (angle: number, dist = step) => async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => { await turn(nid, '', angle); return walk(nid, dist); }, bridge);
    };
    const jdef = (angle: number, dist = step) => async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => { await turn(nid, '', angle); return jump(nid, dist); }, bridge);
    };

    // walk (sans id)
    set('walk', async (d = step) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        console.log(id)
        if (id == null) return;
        return queueOp(id, (nid) => walk(nid, d), bridge);
    });
    set('walk_right', wdef(deg.right));
    set('walk_left', wdef(deg.left));
    set('walk_up', wdef(deg.up));
    set('walk_down', wdef(deg.down));
    set('walk_right_up', wdef(deg.ru, diag));
    set('walk_right_down', wdef(deg.rd, diag));
    set('walk_left_up', wdef(deg.lu, diag));
    set('walk_left_down', wdef(deg.ld, diag));

    set('walk_forward', async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => walk(nid, step), bridge);
    });
    set('walk_backward', async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => { await turn(nid, 'BACKWARD', 0); return walk(nid, step); }, bridge);
    });
    set('walk_choice', async (dir: 'FORWARD' | 'BACKWARD' = 'FORWARD') => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => {
            if (dir === 'FORWARD') return walk(nid, step);
            await turn(nid, 'BACKWARD', 0); return walk(nid, step);
        }, bridge);
    });
    set('walk_choice_by_unit', async (dir: 'FORWARD' | 'BACKWARD', d: number) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => {
            if (dir === 'FORWARD') return walk(nid, d);
            await turn(nid, 'BACKWARD', 0); return walk(nid, d);
        }, bridge);
    });

    // jump (sans id)
    set('jump', async (h = step) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => jump(nid, h), bridge);
    });
    set('jump_right', jdef(deg.right));
    set('jump_left', jdef(deg.left));
    set('jump_up', jdef(deg.up));
    set('jump_down', jdef(deg.down));
    set('jump_right_up', jdef(deg.ru, diag));
    set('jump_right_down', jdef(deg.rd, diag));
    set('jump_left_up', jdef(deg.lu, diag));
    set('jump_left_down', jdef(deg.ld, diag));

    set('jump_forward', async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => jump(nid, step), bridge);
    });
    set('jump_backward', async () => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => { await turn(nid, 'BACKWARD', 0); return jump(nid, step); }, bridge);
    });
    set('jump_choice', async (dir: 'FORWARD' | 'BACKWARD' = 'FORWARD') => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => {
            if (dir === 'FORWARD') return jump(nid, step);
            await turn(nid, 'BACKWARD', 0); return jump(nid, step);
        }, bridge);
    });
    set('jump_choice_by_unit', async (dir: 'FORWARD' | 'BACKWARD', d: number) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, async (nid) => {
            if (dir === 'FORWARD') return jump(nid, d);
            await turn(nid, 'BACKWARD', 0); return jump(nid, d);
        }, bridge);
    });

    // turn (sans id)
    set('turn', async (angle = 90) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => turn(nid, '', angle), bridge);
    });
    set('turn_right_or_left', async (dir: 'RIGHT' | 'LEFT' = 'RIGHT') => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => turn(nid, dir, 90), bridge);
    });
    set('choose_turn_angle', async (dir: 'RIGHT' | 'LEFT' = 'RIGHT', angle = 90) => {
        const id = getDefaultIdFast(bridge) ?? await resolveDefaultIdAsync(bridge);
        if (id == null) return;
        return queueOp(id, (nid) => turn(nid, dir, angle), bridge);
    });

    return asyncNames;
}

// ------- events -------
const hasBridgeEvents = (bridge: EngineBridge) =>
    !!bridge && typeof (bridge as any).events?.on === 'function' && typeof (bridge as any).events?.off === 'function';

function subscribeGlobal(name: string, handler: (...args: any[]) => void): ListenerDisposer {
    const w = window as any;
    if (w.__TILMI_EVENTS__?.on) { const off = w.__TILMI_EVENTS__.on(name, handler); return () => w.__TILMI_EVENTS__.off?.(off); }
    const domEvt = name.startsWith('tilmi:') ? name : `tilmi:${name}`;
    const wrapped = (e: Event) => { const d = (e as CustomEvent).detail; Array.isArray(d) ? handler(...d) : handler(d); };
    window.addEventListener(domEvt, wrapped as EventListener);
    return () => window.removeEventListener(domEvt, wrapped as EventListener);
}
function subscribeEither(
    bridge: EngineBridge,
    name: string,
    handler: (...args: any[]) => void,
    registerDisposer: (d: ListenerDisposer) => void,
) {
    if (hasBridgeEvents(bridge)) {
        const on = (bridge as any).events.on as (n: string, h: (...a: any[]) => void) => void;
        const off = (bridge as any).events.off as (n: string, h: (...a: any[]) => void) => void;
        on(name, handler); registerDisposer(() => off(name, handler));
    } else {
        registerDisposer(subscribeGlobal(name, handler));
    }
}

// --------- Auto-await helper (Python code transform) ----------
// --------- Auto-await helper (Python code transform) ----------
function autoAwaitPython(code: string, asyncNames: string[]): string {
    if (!asyncNames.length) return code;

    const baseNameSet = new Set(asyncNames);
    let lines = code.split('\n');
    const knownAsyncNames = new Set(baseNameSet);

    const runPass = (input: string[]): { lines: string[]; discovered: string[] } => {
        const out = [...input];
        const stack: Array<{ indent: number; index: number; hasAwait: boolean; alreadyAsync: boolean; name?: string }> = [];
        const newlyAsync: string[] = [];

        const flushStack = (indent: number, nonEmpty: boolean) => {
            while (stack.length) {
                const top = stack[stack.length - 1];
                if (indent > top.indent) break;
                if (indent === top.indent && !nonEmpty) break;
                stack.pop();
                if (!top.hasAwait) continue;

                if (top.name && !knownAsyncNames.has(top.name)) {
                    newlyAsync.push(top.name);
                }
                if (!top.alreadyAsync) {
                    const original = out[top.index];
                    if (!/^\s*async\s+def\b/.test(original)) {
                        out[top.index] = original.replace(/^(\s*)def\s+/, '$1async def ');
                    }
                }
            }
        };

        for (let i = 0; i < out.length; i++) {
            let line = out[i];
            const indentMatch = line.match(/^(\s*)/);
            const indent = indentMatch ? indentMatch[1] : '';
            const trimmed = line.slice(indent.length);
            const meaningful = trimmed.trim().length > 0 && !trimmed.trim().startsWith('#');

            flushStack(indent.length, meaningful);

            const defMatch = trimmed.match(/^(async\s+)?def\s+([A-Za-z_][A-Za-z0-9_]*)\s*\(/);
            if (defMatch) {
                const alreadyAsync = Boolean(defMatch[1]);
                const name = defMatch[2];
                stack.push({ indent: indent.length, index: i, hasAwait: false, alreadyAsync, name });
            }

            const controlMatch = line.match(/^(\s*)(if|elif|while)(\s+)(.*?)(\s*:\s*(#.*)?)$/);
            if (controlMatch) {
                const [, ctrlIndent, keyword, spacing, condition, suffix] = controlMatch;
                const condLeadingMatch = condition.match(/^(\s*)/);
                const condTrailingMatch = condition.match(/(\s*)$/);
                const condLeading = condLeadingMatch ? condLeadingMatch[1] : '';
                const condTrailing = condTrailingMatch ? condTrailingMatch[1] : '';
                let core = condition.slice(condLeading.length, condition.length - condTrailing.length);

                if (!/\bawait\b/.test(core)) {
                    let notPrefix = false;
                    if (core.startsWith('not ')) {
                        notPrefix = true;
                        core = core.slice(4).trimStart();
                    }

                    const parenIndex = core.indexOf('(');
                    const lastParenIndex = core.lastIndexOf(')');
                    if (parenIndex > 0 && lastParenIndex === core.length - 1) {
                        const name = core.slice(0, parenIndex).trim();
                        const args = core.slice(parenIndex);
                        if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(name) && knownAsyncNames.has(name)) {
                            const awaitedCall = `await ${name}${args}`;
                            const newCore = notPrefix ? `not (${awaitedCall})` : awaitedCall;
                            const newCondition = `${condLeading}${newCore}${condTrailing}`;
                            line = `${ctrlIndent}${keyword}${spacing}${newCondition}${suffix ?? ':'}`;
                            out[i] = line;
                            if (stack.length) stack[stack.length - 1].hasAwait = true;
                            continue;
                        }
                    }
                }
            }

            const trimmedLeft = line.trimStart();
            if (/^await\s+/.test(trimmedLeft)) {
                if (stack.length) stack[stack.length - 1].hasAwait = true;
                continue;
            }

            const callMatch = line.match(/^(\s*)([A-Za-z_][A-Za-z0-9_]*)\s*\(/);
            if (!callMatch) continue;

            const [, callIndent, name] = callMatch;
            if (!knownAsyncNames.has(name)) continue;

            line = `${callIndent}await ${line.slice(callIndent.length)}`;
            out[i] = line;
            if (stack.length) stack[stack.length - 1].hasAwait = true;
        }

        flushStack(0, true);
        return { lines: out, discovered: newlyAsync };
    };

    while (true) {
        const { lines: nextLines, discovered } = runPass(lines);
        lines = nextLines;
        if (!discovered.length) break;
        let added = false;
        for (const name of discovered) {
            if (!knownAsyncNames.has(name)) {
                knownAsyncNames.add(name);
                added = true;
            }
        }
        if (!added) break;
    }

    // second pass to ensure newly-discovered async functions are awaited everywhere
    if (knownAsyncNames.size > baseNameSet.size) {
        lines = runPass(lines).lines;
    }

    return lines.join('\n');
}

export const PYRuntimeGlue: PYGlue = {
    async compile(code: string, bridge: EngineBridge, _controls: Controls) {
        const pyodide = await getPyodide();

        const functionMap = resolveFunctionMap(bridge);
        const asyncActionNames = overrideMovementForPython(functionMap, bridge); // NEW: on récupère les noms async exposés

        const fnNames = Object.keys(functionMap);

        const caller = (name: string, ...args: any[]) => {
            const entry = (functionMap as any)[name] as FnEntry | undefined;
            if (!entry || typeof entry.fn !== 'function') throw new Error(`Unknown API method: ${name}`);
            const g = globalThis as any;
            const prevEngine = g.engine, prevBridge = g.__IDE_BRIDGE__;
            if (bridge && g.engine !== bridge) g.engine = bridge;
            if (bridge && g.__IDE_BRIDGE__ !== bridge) g.__IDE_BRIDGE__ = bridge;
            try { return entry.fn.apply(entry.owner, args); }
            finally {
                if (prevEngine === undefined) delete g.engine; else g.engine = prevEngine;
                if (prevBridge === undefined) delete g.__IDE_BRIDGE__; else g.__IDE_BRIDGE__ = prevBridge;
            }
        };

        const listenerDisposers: ListenerDisposer[] = [];
        const pyProxies: any[] = [];
        let dispatchEvent: any;

        const __register_event = (eventName: string, pyFn: any, eventParams: any[] = []) => {
            const pyFnOwned = (pyFn && typeof pyFn.copy === 'function') ? pyFn.copy() : pyFn;
            const callPy = (payload: any = {}) => {
                try {
                    const result = dispatchEvent ? dispatchEvent(pyFnOwned, payload) : pyFnOwned(payload);
                    if (result && typeof (result as any).then === 'function') {
                        (result as Promise<any>).catch((e) => console.error('[PY-Event] handler', e));
                    }
                } catch (e) {
                    console.error('[PY-Event] handler', e);
                }
            };

            if (eventName === 'keyPress') {
                const keys = Array.isArray(eventParams) ? eventParams.map(String) : [];
                const fn = (e: KeyboardEvent) => { if (keys.length === 0 || keys.includes(e.key)) callPy({ key: e.key }); };
                // Use capture phase to ensure events are received even if editor stops bubbling
                window.addEventListener('keydown', fn, true);
                listenerDisposers.push(() => window.removeEventListener('keydown', fn, true as any));
                return;
            }
            if (eventName === 'timerOnce' || eventName === 'timerDuring') {
                const seconds = Number(eventParams?.[0] ?? 0);
                if (!Number.isFinite(seconds) || seconds <= 0) return console.warn(`[PY-Event] bad timer (${eventParams?.[0]})`);
                const cb = () => callPy({ seconds });
                if (eventName === 'timerOnce') { const id = setTimeout(cb, seconds * 1000); listenerDisposers.push(() => clearTimeout(id as any)); }
                else { const id = setInterval(cb, seconds * 1000); listenerDisposers.push(() => clearInterval(id as any)); }
                return;
            }

            const add = (name: string, fn: (...a: any[]) => void) => subscribeEither(bridge, name, fn, (d) => listenerDisposers.push(d));

            if (eventName === 'collision') {
                const [watchA, watchB] = eventParams ?? [];
                const fn = (idA: string, idB: string) => {
                    const okA = !watchA || watchA === '*' || idA === String(watchA) || idB === String(watchA);
                    const okB = !watchB || watchB === '*' || idA === String(watchB) || idB === String(watchB);
                    if (okA && okB) callPy({ entityA: idA, entityB: idB });
                };
                add('collisionEvent', fn);
                const fnPick = (p: { charId: string; objectId: string }) => fn(p.charId, p.objectId);
                add('collision:char-pick', fnPick);
                return;
            }
            if (eventName === 'textChange') {
                const [watchIdRaw = '*'] = eventParams ?? ['*'];
                const fn = (ev: { textId: string; newText: string; oldText: string }) => { if (watchIdRaw === '*' || ev.textId === String(watchIdRaw)) callPy(ev); };
                add('textChangeEvent', fn);
                return;
            }
            if (eventName === 'pickup') {
                const [watchChar, watchObj] = eventParams ?? [];
                const fn = (charId: string, objId: string) => {
                    const okChar = !watchChar || watchChar === '*' || charId === String(watchChar);
                    const okObj = !watchObj || watchObj === '*' || objId === String(watchObj);
                    if (okChar && okObj) callPy({ charId, objId });
                };
                add('pickupEvent', fn);
                return;
            }
            console.warn(`(registerEvent) unknown: ${eventName}`);
        };

        const py = pyodide;
        py.globals.set('__call_js__', caller);
        py.globals.set('__exports__', fnNames);
        py.globals.set('__register_event', __register_event);

        await py.runPythonAsync(`
import asyncio, inspect

async def __dispatch_event(fn, ev=None):
    try:
        if hasattr(ev, "to_py"):
            ev = ev.to_py()
    except Exception:
        pass
    result = fn(ev)
    if inspect.isawaitable(result):
        return await result
    return result

def _wrap_handler(fn):
    return fn

def on(event_name, *event_params):
    def _decorator(fn):
        __register_event(event_name, _wrap_handler(fn), list(event_params))
        return fn
    return _decorator

def on_key_press(*keys): return on('keyPress', *keys)
def timer_once(seconds): return on('timerOnce', seconds)
def timer_during(seconds): return on('timerDuring', seconds)
def on_collision(watchA=None, watchB=None):
    params = []
    if watchA is not None: params.append(watchA)
    if watchB is not None: params.append(watchB)
    return on('collision', *params)
def on_text_change(watch_id='*'): return on('textChange', watch_id)
def on_pickup(watch_char=None, watch_obj=None):
    params = []
    if watch_char is not None: params.append(watch_char)
    if watch_obj is not None: params.append(watch_obj)
    return on('pickup', *params)
`);
        dispatchEvent = py.globals.get('__dispatch_event');
        if (dispatchEvent) {
            pyProxies.push(dispatchEvent);
        }

        const executionState = { isRunning: false, isPaused: false, shouldStop: false };

        return {
            pyodide,
            code,
            functionMap,
            fnNames,
            caller,
            listenerDisposers,
            pyProxies,
            executionState,
            bridge,
            asyncActionNames, // NEW: stocké pour auto-await
            dispose() {
                try { this.listenerDisposers.forEach((f: ListenerDisposer) => f()); } catch { }
                this.listenerDisposers = [];
                try { this.pyProxies.forEach((p: any) => p?.destroy?.()); } catch { }
                this.pyProxies = [];
            },
        };
    },

    async runAll(exec: any, _controls: Controls) {
        const { pyodide, caller } = exec;
        const fwd = makeLogForwarder(exec.bridge);

        pyodide.globals.set('__call_js__', caller);
        pyodide.globals.set('__exports__', Object.keys(exec.functionMap ?? {}));

        await pyodide.runPythonAsync(`
import sys, io, contextlib, builtins
try: stdout_buffer
except NameError: stdout_buffer = io.StringIO()
try: stderr_buffer
except NameError: stderr_buffer = io.StringIO()

@contextlib.contextmanager
def _capture_io():
    global stdout_buffer, stderr_buffer
    old_out, old_err = sys.stdout, sys.stderr
    sys.stdout, sys.stderr = stdout_buffer, stderr_buffer
    try: yield
    finally: sys.stdout, sys.stderr = old_out, old_err

def _reset_buffers():
    global stdout_buffer, stderr_buffer
    stdout_buffer = io.StringIO()
    stderr_buffer = io.StringIO()

def __install_exports(exports):
    try: names = list(exports)
    except Exception: names = []
    g = globals()
    for name in names:
        def _mk(n):
            def f(*args, **kwargs): 
                return __call_js__(n, *args, **kwargs)
            f.__name__ = n
            return f
        func = _mk(name)
        g[name] = func
        try: setattr(builtins, name, func)
        except Exception: pass

__install_exports(__exports__)
del __install_exports
`);

        // --- AUTO-AWAIT ici (côté TS) ---
        const original = String(exec.code ?? '');
        const asyncNames: string[] = Array.isArray(exec.asyncActionNames) ? exec.asyncActionNames : [];
        const transformed = autoAwaitPython(original, asyncNames);

        let error: any = null;
        try {
            await pyodide.runPythonAsync(`_reset_buffers()`);
            // On exécute dans une coroutine top-level pour accepter les "await" insérés
            await pyodide.runPythonAsync(`
ns = globals()
with _capture_io():
    async def __user_main__():
${transformed.split('\n').map(l => '        ' + l).join('\n')}
        # Ensure variables defined at top-level become globals for event handlers
        try:
            _g = globals()
            for _k, _v in locals().items():
                if not str(_k).startswith('_'):
                    _g[_k] = _v
        except Exception:
            pass
    await __user_main__()
del ns
`);
        } catch (e: any) {
            error = e;
            console.log(error)
        } finally {
            try {
                const stdout = String(pyodide.runPython(`stdout_buffer.getvalue()`));
                const stderr = String(pyodide.runPython(`stderr_buffer.getvalue()`));
                if (stdout.trim().length) fwd.log(stdout);
                if (stderr.trim().length) fwd.error(stderr);
            } catch { }
            fwd.complete();
        }
    },

    async stop(exec: any) { try { exec.dispose?.(); } catch { } },
};
